import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import channelService from '../services/channelService';
import { toast } from 'react-toastify';
import Sidebar from '../components/Sidebar';
import ThemeToggle from '../components/ThemeToggle';
import { Helmet } from 'react-helmet-async';
import { useTheme } from '../contexts/ThemeContext';
import './Profile.css';

function Profile() {
  const { username } = useParams();
  const { theme } = useTheme();
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedChannels, setExpandedChannels] = useState({});
  
  const isOwnProfile = currentUser && currentUser.username === username;
  
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const profileData = await channelService.getPublicProfile(username);
        setProfile(profileData);
      } catch (err) {
        setError(typeof err.response?.data?.detail === 'string' 
          ? err.response.data.detail 
          : 'Profile not found');
        toast.error('Could not load profile');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProfile();
  }, [username]);
  
  const toggleDescription = (channelId) => {
    setExpandedChannels(prev => ({
      ...prev,
      [channelId]: !prev[channelId]
    }));
  };
  
  if (loading) {
    return <div className="profile-loading">Loading profile...</div>;
  }
  
  if (error || !profile) {
    return (
      <div className="profile-error">
        <div className="container">
          <div className="error-card">
            <svg viewBox="0 0 24 24" width="48" height="48" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <h1>Profile Not Found</h1>
            <p>{typeof error === 'string' ? error : 'The profile you are looking for does not exist or is not public.'}</p>
            <Link to="/" className="btn btn-primary">Go Home</Link>
          </div>
        </div>
      </div>
    );
  }
  
  const pageTitle = `${profile.name || profile.username}'s Profile | Channls`;
  const pageDescription = profile.description || `Check out ${profile.name || profile.username}'s Telegram channels on Channls`;
  const pageUrl = `https://channls.onrender.com/u/${username}`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={profile.avatar_url || `https://channls.onrender.com/logo${theme === 'dark' ? '' : '_dark'}.png`} />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content={pageTitle} />
        <meta property="twitter:description" content={pageDescription} />
        <meta property="twitter:image" content={profile.avatar_url || `https://channls.onrender.com/logo${theme === 'dark' ? '' : '_dark'}.png`} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={pageUrl} />
      </Helmet>

      <div className="app-container">
        {currentUser && <Sidebar />}
        <div className="profile-page">
          <div className="profile-header">
            <div className="profile-avatar">
              {profile.name ? profile.name.charAt(0).toUpperCase() : username.charAt(0).toUpperCase()}
            </div>
            <div className="profile-info">
              <h1 className="profile-name">{profile.name || username}</h1>
              <div className="profile-username">@{username}</div>
              {profile.telegram_username && (
                <a 
                  href={`https://t.me/${profile.telegram_username}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="telegram-link"
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.198 2.433a2.242 2.242 0 0 0-1.022.215l-8.609 3.33c-2.068.8-4.133 1.598-5.724 2.21a405.15 405.15 0 0 1-2.849 1.09c-.42.147-.99.332-1.473.901-.728.968.193 1.798.919 2.286 1.61.516 3.275 1.009 4.654 1.472.846 1.467 1.683 2.986 2.561 4.41.362.77 1.194.772 1.564.03.736-1.471 1.321-2.714 1.94-3.979.104-.21.202-.108.355.1 2.354 3.552 5.006 6.86 6.843 8.738.472.479 1.7.463 2.198-.003.497-.467.46-1.266.052-1.744-2.432-2.97-4.818-6.135-7.097-9.47a169.91 169.91 0 0 0-.54-.666c.223-.1.442-.196.653-.289 2.686-1.202 5.74-2.56 8.159-3.738.169-.086.565-.333.518-.839-.038-.508-.49-.655-.802-.762z" />
                  </svg>
                  {profile.telegram_username}
                </a>
              )}
            </div>
            
            <div className="profile-actions">
              <ThemeToggle />
              {isOwnProfile && (
                <Link to="/dashboard" className="btn btn-secondary">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                  Edit Profile
                </Link>
              )}
            </div>
          </div>
          
          {profile.description && (
            <div className="profile-bio">
              <h3>Description</h3>
              <p>{profile.description}</p>
            </div>
          )}
          
          {profile.channels && profile.channels.length > 0 ? (
            <div className="channels-section">
              <h2>Telegram Channels</h2>
              <div className="channels-list">
                {profile.channels.map(channel => (
                  <div key={channel.id} className="channel-item" onClick={() => window.open(channel.url, '_blank', 'noopener,noreferrer')}>
                    <div className="channel-icon">
                      {channel.avatar_url ? (
                        <img src={channel.avatar_url} alt={channel.title || 'Channel'} className="channel-avatar" />
                      ) : (
                      <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21.198 2.433a2.242 2.242 0 0 0-1.022.215l-8.609 3.33c-2.068.8-4.133 1.598-5.724 2.21a405.15 405.15 0 0 1-2.849 1.09c-.42.147-.99.332-1.473.901-.728.968.193 1.798.919 2.286 1.61.516 3.275 1.009 4.654 1.472.846 1.467 1.683 2.986 2.561 4.41.362.77 1.194.772 1.564.03.736-1.471 1.321-2.714 1.94-3.979.104-.21.202-.108.355.1 2.354 3.552 5.006 6.86 6.843 8.738.472.479 1.7.463 2.198-.003.497-.467.46-1.266.052-1.744-2.432-2.97-4.818-6.135-7.097-9.47a169.91 169.91 0 0 0-.54-.666c.223-.1.442-.196.653-.289 2.686-1.202 5.74-2.56 8.159-3.738.169-.086.565-.333.518-.839-.038-.508-.49-.655-.802-.762z" />
                      </svg>
                      )}
                    </div>
                    
                    <div className="channel-details">
                      <div className="channel-top">
                        <h3>{channel.title || 'Telegram Channel'}</h3>
                      </div>
                      
                      <div className="channel-meta">
                        <svg className="external-icon" viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                          <polyline points="15 3 21 3 21 9"></polyline>
                          <line x1="10" y1="14" x2="21" y2="3"></line>
                        </svg>
                        <span>Telegram Channel</span>
                        
                        {channel.subscribers_count && (
                          <span className="subscribers-count">
                            <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                              <circle cx="9" cy="7" r="4"></circle>
                              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                            </svg>
                            {channel.subscribers_count.toLocaleString()}
                          </span>
                        )}
                        
                        {channel.description && (
                          <button 
                            className="info-icon" 
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDescription(channel.id);
                            }}
                            title={expandedChannels[channel.id] ? "Hide info" : "Show info"}
                          >
                            {expandedChannels[channel.id] ? (
                              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="16" x2="12" y2="12"></line>
                                <line x1="12" y1="8" x2="12.01" y2="8"></line>
                              </svg>
                            ) : (
                              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="16" x2="12" y2="12"></line>
                                <line x1="12" y1="8" x2="12.01" y2="8"></line>
                              </svg>
                            )}
                          </button>
                        )}
                      </div>
                      
                      {channel.description && expandedChannels[channel.id] && (
                        <div className="channel-description">
                          <p>{channel.description}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <svg viewBox="0 0 24 24" width="48" height="48" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="12" y1="8" x2="12" y2="16"></line>
                <line x1="8" y1="12" x2="16" y2="12"></line>
              </svg>
              <p>No channels found</p>
              <p className="empty-state-tip">This user hasn't added any public channels yet</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Profile; 